import React, { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, Copy, Check } from "lucide-react";
import useCaseTicket from "./useCaseTicket";
import {
  BTN,
  CARD,
  LINK,
  BADGE,
  NEUTRAL,
  Reader,
  Section,
  Row,
  MatchRing,
  Loader,
  Message,
  fmtDate,
} from "../bugs/BugTicketPage";

const COLLAPSED_AT = 1200; // characters before a long text collapses

// Long-text fields shown as sections, in reading order. Each is counted for completeness.
const SECTIONS = [
  ["symptoms", "Reported Symptoms"],
  ["troubleshooting", "Troubleshooting"],
  ["resolutionSteps", "Resolution Steps"],
  ["rootCause", "Root Cause"],
  ["resolution", "Resolution"],
];

const CopyButton = ({ text, label = "Copy" }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text || "");
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch {
      // clipboard unavailable: nothing to do
    }
  };
  return (
    <button type="button" className={BTN} onClick={copy} aria-label={label}>
      {done ? <Check size={13} /> : <Copy size={13} />} {done ? "Copied" : label}
    </button>
  );
};

const Collapsible = ({ text, children }) => {
  const long = (text || "").length > COLLAPSED_AT;
  const [open, setOpen] = useState(false);
  return (
    <div>
      <div className={long && !open ? "max-h-72 overflow-hidden relative" : ""} data-testid="collapsible">
        {children}
        {long && !open && (
          <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white to-transparent" />
        )}
      </div>
      <div className="flex gap-2 mt-2">
        {long && (
          <button type="button" className={BTN} onClick={() => setOpen((o) => !o)}>
            {open ? "Show less" : "Show more"}
          </button>
        )}
      </div>
    </div>
  );
};

const Warnings = ({ items }) =>
  items?.length > 0 ? (
    <div
      className="flex gap-2 px-3 py-2.5 mb-4 text-[13px] rounded-md border border-amber-500 bg-amber-50 text-amber-800"
      role="status"
    >
      <AlertTriangle size={15} className="shrink-0 mt-0.5" />
      <ul className="m-0 p-0 list-none">
        {items.map((w) => (
          <li key={w}>{w}</li>
        ))}
      </ul>
    </div>
  ) : null;

const RawChunks = ({ chunks }) => (
  <div className="flex flex-col gap-2">
    {chunks.map((c) => (
      <details key={c.pointId} className="border border-gray-200 rounded-md bg-white">
        <summary className="cursor-pointer px-3 py-2 text-[13px]">
          Chunk {c.chunkIndex} · {c.length} chars · <code>{c.pointId}</code>
        </summary>
        <div className="px-3 pb-3">
          <div className="mb-2">
            <CopyButton text={c.text} label="Copy chunk" />
          </div>
          <pre className="m-0 text-xs whitespace-pre-wrap break-words">{c.text}</pre>
        </div>
      </details>
    ))}
  </div>
);

export default function CaseTicketPage() {
  const { caseId: rawId } = useParams();
  const caseId = rawId || "";
  const [params] = useSearchParams();
  const { data, loading, error, notFound, retry } = useCaseTicket(caseId);
  const [tab, setTab] = useState("case");

  const match = Number(params.get("match"));
  const hasMatch = params.has("match") && Number.isFinite(match);

  const hasChunks = (data?.chunks?.length || 0) > 0;
  const tabs = [
    ["case", "Case"],
    ...(hasChunks ? [["chunks", "Raw Chunks"]] : []),
  ];

  return (
    <div className="w-full min-h-screen self-stretch bg-[#FAFAF6] text-[#253439]">
      <header className="flex items-center justify-between h-12 px-6 bg-white border-b border-gray-200">
        <img src="/A_bgless.png" alt="Arta" className="h-7" />
      </header>

      {loading && <Loader label="Loading case…" />}
      {notFound && <Message title="Case not found" text={`No case exists for “${caseId}”.`} />}
      {error && (
        <Message
          title="Couldn’t load case"
          text={error}
          action={
            <button type="button" className={BTN} onClick={retry}>
              Retry
            </button>
          }
        />
      )}

      {data && (
        <main className="max-w-[1280px] mx-auto px-6 pt-5 pb-12">
          <nav aria-label="Breadcrumb" className="text-[13px] text-gray-500 mb-3">
            <Link to="/TseDashboard3/new" className={LINK}>
              Cases
            </Link>
            {data.product && <> / <span>{data.product}</span></>} / <span>{data.caseNumber || data.caseId}</span>
          </nav>

          <div className="flex flex-wrap items-start justify-between gap-6 mb-4">
            <div>
              <p className="text-[13px] font-semibold text-gray-500 mb-1">Case {data.caseNumber || data.caseId}</p>
              <h1 className="text-2xl font-bold leading-snug mb-3">{data.title || `Case ${data.caseNumber || data.caseId}`}</h1>
              <div className="flex flex-wrap items-center gap-2">
                {data.product && <span className={`${BADGE} border-cyan-200 text-cyan-700 bg-cyan-50`}>{data.product}</span>}
                {data.namespace && <span className={`${BADGE} ${NEUTRAL}`}>{data.namespace}</span>}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {hasMatch && <MatchRing pct={Math.max(0, Math.min(100, Math.round(match)))} />}
            </div>
          </div>

          <Warnings items={data.warnings} />

          <div role="tablist" className="flex gap-1 mb-4 border-b border-gray-200">
            {tabs.map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                onClick={() => setTab(id)}
                className={`px-3 py-2 text-[13px] -mb-px border-b-2 ${
                  tab === id ? "border-cyan-500 text-[#253439] font-semibold" : "border-transparent text-gray-500"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === "case" && (
            <div className="grid gap-4 items-start lg:grid-cols-[2fr_1fr]">
              <div className="flex flex-col gap-4 min-w-0">
                <Section title="Description">
                  <Collapsible text={data.description}>
                    <Reader id="ct-description" text={data.description} />
                  </Collapsible>
                </Section>
                {SECTIONS.map(
                  ([key, title]) =>
                    data[key] && (
                      <Section key={key} title={title}>
                        <Collapsible text={data[key]}>
                          <Reader text={data[key]} />
                        </Collapsible>
                      </Section>
                    )
                )}
              </div>
              <aside aria-label="Details" className="order-last">
                <section className={CARD}>
                  <h2 className="text-sm font-semibold mb-3">Details</h2>
                  <dl className="m-0">
                    <Row label="Case Number">{data.caseNumber}</Row>
                    <Row label="Case ID">{data.caseId}</Row>
                    <Row label="Product">{data.product}</Row>
                    <Row label="Namespace">{data.namespace}</Row>
                    <Row label="Created">{data.createdAt && fmtDate(data.createdAt)}</Row>
                    <Row label="Source">{data.source}</Row>
                    <Row label="Chunks">{data.chunkCount > 0 && `${data.chunksFound} / ${data.chunkCount}`}</Row>
                  </dl>
                </section>
              </aside>
            </div>
          )}

          {tab === "chunks" && hasChunks && (
            <Section title="Raw Chunks">
              <RawChunks chunks={data.chunks} />
            </Section>
          )}
        </main>
      )}
    </div>
  );
}
